import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useMutation } from '@apollo/client';
import { SET_WHATSAPP } from '../../graphql/feedback';
import { useError } from '../../hooks/useError.js';
import { useToast } from '../../hooks/useToast.js';

const CONSENT_TEXT =
  'Aceito receber mensagens via WhatsApp sobre o acompanhamento das minhas contribuições (status, respostas a dúvidas, validações), avisos sobre minhas recompensas e novidades da plataforma. Posso revogar este consentimento a qualquer momento em Configurações.';

const getBrPhoneDigits = (raw) => {
  const digits = (raw || '').replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length >= 12) return digits.slice(2, 13);
  return digits.slice(0, 11);
};

const formatBr = (raw) => {
  const digits = getBrPhoneDigits(raw);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const WhatsappConsentModal = ({ isOpen, initialValue, onClose, onSaved, isDarkTheme }) => {
  const [phone, setPhone] = useState(initialValue || '');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(null);
  const { showError } = useError();
  const toast = useToast();
  const [setWhatsapp, { loading }] = useMutation(SET_WHATSAPP);

  useEffect(() => {
    if (isOpen) {
      setPhone(initialValue || '');
      setAccepted(false);
      setError(null);
    }
  }, [initialValue, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    const digits = getBrPhoneDigits(phone);
    if (digits.length < 10 || digits.length > 11) {
      setError('Informe um número brasileiro válido com DDD.');
      return;
    }
    if (!accepted) {
      setError('Você precisa aceitar receber as mensagens para continuar.');
      return;
    }
    try {
      await setWhatsapp({ variables: { whatsapp: digits, optIn: true } });
      toast.success('WhatsApp salvo com sucesso.');
      onSaved?.();
    } catch (err) {
      setError(showError(err, { fallback: 'Falha ao salvar WhatsApp.' }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={clsx(
          'w-full max-w-md rounded-2xl shadow-xl p-6 space-y-4',
          isDarkTheme ? 'bg-greenbook-dark-bg text-greenbook-dark-text' : 'bg-white text-gray-900'
        )}
      >
        <h2 className="text-xl font-semibold">Acompanhamento por WhatsApp</h2>
        <p className="text-sm">
          Para enviar contribuições e receber respostas, precisamos de um WhatsApp e do seu consentimento.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">WhatsApp (com DDD)</label>
            <input
              type="tel"
              autoComplete="tel"
              className={clsx(
                'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2',
                isDarkTheme
                  ? 'bg-gray-950/30 border-gray-700 focus:ring-greenbook-dark-primary'
                  : 'bg-white border-gray-300 focus:ring-greenbook-light-primary'
              )}
              placeholder="(11) 91234-5678"
              value={formatBr(phone)}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <label className="flex gap-2 items-start text-xs">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>{CONSENT_TEXT}</span>
          </label>
          {error && <p className="text-sm text-red-600 dark:text-red-300">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className={clsx('px-4 py-2 rounded-lg text-sm', isDarkTheme ? 'hover:bg-gray-800' : 'hover:bg-gray-100')}
              onClick={onClose}
              disabled={loading}
            >
              Depois
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-greenbook-light-primary text-white hover:bg-greenbook-light-green hover:text-green-900 disabled:opacity-60"
              disabled={loading}
            >
              {loading ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

WhatsappConsentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  initialValue: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  isDarkTheme: PropTypes.bool,
};

WhatsappConsentModal.defaultProps = {
  initialValue: '',
  onSaved: undefined,
  isDarkTheme: false,
};

export default WhatsappConsentModal;
